import { Anchor, Box, Breadcrumbs, Container, Divider, Group, LoadingOverlay, Space, Stack, Tabs } from '@mantine/core';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndProvider } from 'react-dnd-latest';
import { HTML5Backend } from 'react-dnd-html5-backend-latest';
import { IconPilcrow, IconSettings } from '@tabler/icons';
import { QuestionnaireTemplateContext } from '../../../Service/Context/QuestionnaireTemplateContext';
import { RichInput } from '../../../Organismns/tiptap/TiptapEditorInput';
import BookitupTextInput from '../../../Atoms/inputs/BookitupTextInput';
import { TiptapS } from '../../../Service/TiptapS';
import DocumentBubbleMenu from '../../../Organismns/tiptap/toolbars/BubbleMenu';
import SkeletonItems, { QuestionBundleSkeleton } from '../../../Atoms/SkeletonItems';
import LinkText from '../../../Atoms/LinkText';
import QuestionnaireAlert from '../../../Atoms/QuestionnaireAlert';
import DocumentLanguagePicker from '../../../Atoms/inputs/DocumentLanguagePicker';
import QuestionnaireTemplateActions from './QuestionnaireTemplateActions';
import QuestionBundle from './QuestionBundle';
import QuestionnaireTemplateSignatureSection from './QuestionnaireTemplateSignatureSection';

const QuestionnaireTemplateEditor = () => {
  const { loading, insertingBundle, template, patchTemplate, bundleHandlers, questionHandlers } =
    useContext(QuestionnaireTemplateContext);
  const navigate = useNavigate();
  const { insertBundle } = bundleHandlers;
  const { name, subject, headRow, endRow, questionBundles, language } = template ?? { language: 'german' };
  const emptyBundles = questionBundles?.length === 0;

  return (
    <Container p={0} pos="relative">
      <Group position="apart" sx={{ alignItems: 'center' }}>
        <Breadcrumbs>
          <Anchor onClick={() => navigate('/settings/questionnaires')}>Fragebogen-Vorlagen</Anchor>
          <Anchor sx={{ pointerEvents: 'none' }}>
            <b>{loading ? 'Laden ...' : name || 'Nicht gefunden '}</b>
          </Anchor>
        </Breadcrumbs>
        <QuestionnaireTemplateActions />
      </Group>
      <Divider my="xs" />
      <QuestionnaireAlert />
      <Tabs
        styles={{
          tab: {
            '&[data-active]': {
              fontWeight: 'bolder',
            },
          },
        }}
        defaultValue="content"
      >
        <Tabs.List>
          <Tabs.Tab value="content" icon={<IconPilcrow size={14} />}>
            Inhalt
          </Tabs.Tab>
          <Tabs.Tab value="settings" icon={<IconSettings size={14} />}>
            Einstellungen
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="content" pt="xl">
          <BookitupTextInput
            mb="xs"
            label="Betreff"
            defaultValue={subject}
            onBlurSave={(value) => patchTemplate({ subject: value })}
          />
          {headRow !== undefined && (
            <RichInput
              label="Kopfzeile"
              value={headRow}
              patchField={(htmlString) => patchTemplate({ headRow: htmlString })}
              menu={<DocumentBubbleMenu />}
              styles={TiptapS.TextInputStyles}
            />
          )}
          {loading && <SkeletonItems height={100} />}
          <Box pos="relative">
            <LoadingOverlay visible={insertingBundle} overlayBlur={2} loaderProps={{ size: 'xl' }} />
            {!loading && emptyBundles && (
              <>
                <QuestionBundleSkeleton />
                <LinkText mt={5} mb="xl" ml="sm" onClick={() => insertBundle()}>
                  + Abschnitt hinzufügen
                </LinkText>
              </>
            )}
            {!emptyBundles && (
              <DndProvider backend={HTML5Backend}>
                {questionBundles && (
                  <Stack my="xl">
                    {questionBundles.map((qb, index) => (
                      <QuestionBundle
                        key={qb.id}
                        index={index}
                        language={language}
                        bundle={qb}
                        bundleHandlers={bundleHandlers}
                        questionHandlers={questionHandlers}
                      />
                    ))}
                  </Stack>
                )}
              </DndProvider>
            )}
          </Box>
          {endRow !== undefined && (
            <RichInput
              label="Fußzeile"
              value={endRow}
              patchField={(htmlString) => patchTemplate({ endRow: htmlString })}
              menu={<DocumentBubbleMenu />}
              styles={{ minHeight: 150 }}
            />
          )}
          <Space h="xl" />
        </Tabs.Panel>
        <Tabs.Panel
          value="settings"
          pt="xs"
          sx={(theme) => ({ color: theme.colorScheme === 'dark' ? 'white' : 'black' })}
        >
          <Group mt="md">
            <DocumentLanguagePicker
              variant="select"
              language={language}
              onLanguageChange={template ? (newLanguage) => patchTemplate({ language: newLanguage }) : undefined}
            />
          </Group>
          <Divider my={30} />
          <QuestionnaireTemplateSignatureSection />
          <Space h="xl" />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
};

export default QuestionnaireTemplateEditor;
